import { ButtonUpsellProtected } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { MarkAsCompletedConfirmationModal } from '@features/recurringInvoices/elements/MarkAsCompletedConfirmationModal'
import { RecurringInvoiceStatus } from '@features/recurringInvoices/enums/recurringInvoiceStatus'
import { useRecurringInvoiceStatus } from '@features/recurringInvoices/hooks/useRecurringInvoiceStatus'

import { ModalId } from '../../../../../../enums/modalId'
import { Scope } from '../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../hooks'
import { useRecurringInvoice } from '../../contexts/recurringInvoiceContext'

export const RecurringInvoiceViewActions = (): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { open: openMarkAsCompletedConfirmationModal, close: closeMarkAsCompletedConfirmationModal } = useModal(
    ModalId.RecurringInvoiceMarkAsCompletedConfirmationModal,
  )
  const { recurringInvoice, recurringInvoiceId, isLoading } = useRecurringInvoice()
  const {
    resumeRecurringInvoice,
    pauseRecurringInvoice,
    completeRecurringInvoice,
    isProcessing: isChangingStatus,
  } = useRecurringInvoiceStatus()

  const isPaused = !isLoading && recurringInvoice?.status.status === RecurringInvoiceStatus.Paused
  const isOngoing = !isLoading && recurringInvoice?.status.status === RecurringInvoiceStatus.Ongoing
  const isCompleted = !isLoading && recurringInvoice?.status.status === RecurringInvoiceStatus.Completed

  const handleResumeButtonClick = useCallback(() => {
    resumeRecurringInvoice({
      recurringInvoiceId,
      onSuccess: () => {
        track('XXX - Invoicing Core - Recurring invoice resumed')
      },
    })
  }, [resumeRecurringInvoice, recurringInvoiceId, track])

  const handlePauseButtonClick = useCallback(() => {
    pauseRecurringInvoice({
      recurringInvoiceId,
      onSuccess: () => {
        track('XXX - Invoicing Core - Recurring invoice paused')
      },
    })
  }, [pauseRecurringInvoice, recurringInvoiceId, track])

  const handleMarkAsCompletedButtonClick = useCallback(() => {
    openMarkAsCompletedConfirmationModal()
  }, [openMarkAsCompletedConfirmationModal])

  const handleMarkAsCompleted = useCallback(() => {
    completeRecurringInvoice({ recurringInvoiceId, onSuccess: closeMarkAsCompletedConfirmationModal })
  }, [closeMarkAsCompletedConfirmationModal, completeRecurringInvoice, recurringInvoiceId])

  if (isLoading || isCompleted) {
    return null
  }

  return (
    <>
      <ButtonsGroup>
        {isPaused && (
          <ButtonUpsellProtected
            disabled={isChangingStatus}
            icon="play"
            loading={isChangingStatus}
            onClick={handleResumeButtonClick}
            trackingContext={TrackingContext.CreateRecurringInvoice}
            upsellScope={Scope.RecurringInvoiceUpsell}
            variant="outlined"
          >
            {t('recurring_invoices.page.side_actions.resume')}
          </ButtonUpsellProtected>
        )}

        {isOngoing && (
          <Button
            disabled={isChangingStatus}
            icon="pause"
            loading={isChangingStatus}
            onClick={handlePauseButtonClick}
            variant="outlined"
          >
            {t('recurring_invoices.page.side_actions.pause')}
          </Button>
        )}
        <Button
          disabled={isChangingStatus}
          icon="circleCheck"
          loading={isChangingStatus}
          onClick={handleMarkAsCompletedButtonClick}
          variant="outlined"
        >
          {t('recurring_invoices.page.side_actions.mark_as_completed')}
        </Button>
      </ButtonsGroup>
      <MarkAsCompletedConfirmationModal
        id={ModalId.RecurringInvoiceMarkAsCompletedConfirmationModal}
        isProcessing={isChangingStatus}
        onOk={handleMarkAsCompleted}
      />
    </>
  )
}
